import React, { useMemo, useState } from "react";
import axios from "axios";

const normalizePhone = (value) => value.replace(/\D/g, "").slice(0, 11);

const AccountDeletionRequest = () => {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    reason: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const isValid = useMemo(() => {
    return (
      form.name.trim().length > 0 &&
      form.phone.trim().length >= 10 &&
      form.reason.trim().length >= 5
    );
  }, [form]);

  const handleChange = (key, value) => {
    setForm((prev) => ({
      ...prev,
      [key]: key === "phone" ? normalizePhone(value) : value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!isValid || submitting) return;

    setSubmitting(true);
    try {
      const payload = new FormData();
      payload.append("name", form.name.trim());
      payload.append("phone", form.phone.trim());
      payload.append("email", form.email.trim());
      payload.append("category", "계정 삭제 요청");
      payload.append("title", "앱 계정 삭제 요청");
      payload.append("content", form.reason.trim());

      await axios.post(
        `${process.env.REACT_APP_FASTAPI_ADS_URL}/help/`,
        payload,
      );

      setSubmitted(true);
    } catch (error) {
      const message =
        error?.response?.data?.detail ||
        error?.response?.data?.message ||
        "요청 접수 중 오류가 발생했습니다. 잠시 후 다시 시도해 주세요.";
      alert(message);
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-dvh bg-[#f7f8fa] px-5 py-10 text-[#111827]">
        <div className="mx-auto max-w-[640px] rounded-[24px] bg-white p-8 shadow-[0_12px_40px_rgba(15,23,42,0.08)]">
          <div className="mb-4 text-[28px] font-[700] leading-[36px]">계정 삭제 요청이 접수되었습니다.</div>
          <p className="text-[15px] leading-[24px] text-[#4b5563]">
            접수된 요청은 운영팀에서 확인 후 순차적으로 처리합니다.
            추가 확인이 필요한 경우 입력하신 연락처로 안내드립니다.
          </p>
          <div className="mt-8 rounded-[16px] bg-[#f3f4f6] p-4 text-[14px] leading-[22px] text-[#4b5563]">
            처리 대상: 앱 계정 및 관련 사용자 데이터 삭제 요청
            <br />
            처리 방식: 본인 확인 후 순차 처리
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-dvh bg-[#f7f8fa] px-5 py-10 text-[#111827]">
      <div className="mx-auto max-w-[640px] rounded-[24px] bg-white p-8 shadow-[0_12px_40px_rgba(15,23,42,0.08)]">
        <div className="mb-2 text-[30px] font-[700] leading-[38px]">계정 삭제 요청</div>
        <p className="mb-8 text-[15px] leading-[24px] text-[#4b5563]">
          앱 외부에서도 계정 삭제를 요청할 수 있도록 제공되는 페이지입니다.
          아래 정보를 입력하면 운영팀에서 본인 확인 후 계정 및 관련 데이터를 순차적으로 삭제합니다.
        </p>

        <form className="space-y-5" onSubmit={handleSubmit}>
          <label className="block">
            <div className="mb-2 text-[14px] font-[600] text-[#374151]">이름</div>
            <input
              type="text"
              value={form.name}
              onChange={(event) => handleChange("name", event.target.value)}
              className="w-full rounded-[14px] border border-[#d1d5db] px-4 py-3 text-[15px] outline-none transition focus:border-[#2563eb]"
              placeholder="이름을 입력해 주세요"
            />
          </label>

          <label className="block">
            <div className="mb-2 text-[14px] font-[600] text-[#374151]">휴대폰 번호</div>
            <input
              type="tel"
              inputMode="numeric"
              value={form.phone}
              onChange={(event) => handleChange("phone", event.target.value)}
              className="w-full rounded-[14px] border border-[#d1d5db] px-4 py-3 text-[15px] outline-none transition focus:border-[#2563eb]"
              placeholder="숫자만 입력해 주세요"
            />
          </label>

          <label className="block">
            <div className="mb-2 text-[14px] font-[600] text-[#374151]">이메일(선택)</div>
            <input
              type="email"
              value={form.email}
              onChange={(event) => handleChange("email", event.target.value)}
              className="w-full rounded-[14px] border border-[#d1d5db] px-4 py-3 text-[15px] outline-none transition focus:border-[#2563eb]"
              placeholder="답변을 받을 이메일이 있으면 입력해 주세요"
            />
          </label>

          <label className="block">
            <div className="mb-2 text-[14px] font-[600] text-[#374151]">요청 내용</div>
            <textarea
              rows={6}
              value={form.reason}
              onChange={(event) => handleChange("reason", event.target.value)}
              className="w-full resize-none rounded-[14px] border border-[#d1d5db] px-4 py-3 text-[15px] outline-none transition focus:border-[#2563eb]"
              placeholder="삭제를 요청하는 계정 정보와 요청 사유를 입력해 주세요 (5글자 이상 작성해주세요)"
            />
          </label>

          <div className="rounded-[16px] bg-[#f3f4f6] p-4 text-[13px] leading-[21px] text-[#4b5563]">
            입력하신 정보는 계정 삭제 요청 처리와 본인 확인 목적에만 사용됩니다.
          </div>

          <button
            type="submit"
            disabled={!isValid || submitting}
            className="w-full rounded-[16px] bg-[#111827] px-4 py-4 text-[15px] font-[600] text-white transition disabled:cursor-not-allowed disabled:bg-[#9ca3af]"
          >
            {submitting ? "접수 중..." : "계정 삭제 요청 접수"}
          </button>
        </form>
      </div>
    </div>
  );
};

export default AccountDeletionRequest;
